import { useState } from "react";
import { Alert, App, Button, Image, Input, Select, Space, Tag, Tooltip, Typography, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getToken } from "../../services/api";
import { useAuth } from "../../services/auth";
import { DataTable, useTableParams } from "../../components/DataTable";
import { fmtBytes, RelativeTime, ShortId, StatusTag } from "../../components/format";

const FILTERS = ["search", "ownerType", "status", "visibility"];

/** 表格行。两处 render 都要用，分开写会让 antd 把 record 推成交集而报错。 */
interface AssetRow {
  id: string;
  status: string;
  usage: string[];
}

/**
 * 替换资产文件。
 *
 * 用 Upload 的 customRequest 而不是内置 action：需要带 Authorization 头，
 * 也需要按统一信封解析错误码。
 */
function ReplaceFileButton({ assetId, onDone }: { assetId: string; onDone: () => void }) {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);

  return (
    <Upload
      showUploadList={false}
      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
      customRequest={async ({ file }) => {
        const form = new FormData();
        form.append("file", file as File);
        setBusy(true);
        try {
          const res = await fetch(`/api/v1/admin/assets/${assetId}/file`, {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken() ?? ""}` },
            body: form,
          });
          const body = await res.json();
          if (!res.ok || body.error) {
            const err = body?.error ?? {};
            // 409 重复内容带 hint（正确做法是让两处引用同一个资产），
            // 只显示 message 的话使用者只知道「不行」，不知道该怎么办。
            message.error(
              err.hint ? `${err.message ?? "上传失败"}\n${err.hint}` : (err.message ?? "上传失败"),
              err.hint ? 8 : 3,
            );
            return;
          }
          message.success("已替换。引用它的立绘/头像自动生效，无需改关联");
          onDone();
        } catch {
          message.error("上传失败");
        } finally {
          setBusy(false);
        }
      }}
    >
      <Button size="small" icon={<UploadOutlined />} loading={busy}>
        替换
      </Button>
    </Upload>
  );
}

export function AssetsPage() {
  const { searchParams, setParam } = useTableParams(FILTERS);
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const storage = useQuery({
    queryKey: ["assetStorage"],
    queryFn: () =>
      api.get<{
        uploadEnabled: boolean;
        previewEnabled: boolean;
        missing: string[];
        bucket: string | null;
        region: string;
        prefix: string;
        maxBytes: number;
      }>("/api/v1/admin/assets/storage"),
  });

  const canUpload = can("asset.write") && storage.data?.uploadEnabled === true;

  return (
    <>
      {storage.data && !storage.data.uploadEnabled && can("asset.write") && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message="现在还不能上传图片"
          description={
            <>
              <div>
                资产表里存的只是文件路径，真正的图片文件要放在对象存储（阿里云 OSS）里。
                还没连上，所以这里既不能上传也看不到预览。
              </div>
              <div style={{ marginTop: 8 }}>
                需要做三件事：<b>①</b> 在阿里云开一个 OSS 存储桶；
                <b>②</b> 建一个只能读写这个桶的子账号，拿到一对密钥；
                <b>③</b> 把桶名和密钥填进后端配置（<Typography.Text code>apps/api/.env</Typography.Text>）后重启。
              </div>
              <div style={{ marginTop: 8 }}>
                当前缺少：<Typography.Text code>{storage.data.missing.join("、")}</Typography.Text>
              </div>
            </>
          }
        />
      )}
      <DataTable
        title="资产"
      path="/api/v1/admin/assets"
      filterKeys={FILTERS}
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {storage.data?.previewEnabled
            ? `图片存在 ${storage.data.bucket} / ${storage.data.prefix}，标记为「pending_upload」的是还没上传真实文件的占位资产`
            : "配置 ASSET_PUBLIC_BASE_URL 后才能预览"}
        </Typography.Text>
      }
      toolbar={
        <Space wrap>
          <Input.Search
            allowClear
            style={{ width: 320 }}
            placeholder="storageKey / 旧 URL"
            defaultValue={searchParams.get("search") ?? ""}
            onSearch={(v) => setParam("search", v)}
          />
          <Select
            allowClear
            style={{ width: 150 }}
            placeholder="归属"
            value={searchParams.get("ownerType") ?? undefined}
            onChange={(v) => setParam("ownerType", v)}
            options={["user", "character", "message", "system"].map((v) => ({ value: v, label: v }))}
          />
          <Select
            allowClear
            style={{ width: 170 }}
            placeholder="状态"
            value={searchParams.get("status") ?? undefined}
            onChange={(v) => setParam("status", v)}
            options={["pending_upload", "scanning", "ready", "quarantined", "deleted"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Select
            allowClear
            style={{ width: 150 }}
            placeholder="可见性"
            value={searchParams.get("visibility") ?? undefined}
            onChange={(v) => setParam("visibility", v)}
            options={["private", "relationship", "public"].map((v) => ({ value: v, label: v }))}
          />
        </Space>
      }
      columns={[
        {
          title: "预览",
          dataIndex: "previewUrl",
          width: 72,
          align: "center",
          /*
           * previewUrl 为空有两种完全不同的原因，必须分开说：
           *   · status = pending_upload —— 文件本来就还没上传，后端刻意不给 URL，
           *     否则会拼出一个必定 404 的地址，看起来像「OSS 配错了」；
           *   · 没配 ASSET_PUBLIC_BASE_URL —— 这才是配置问题。
           * 混成一句话会把「还没传图」误报成「系统没配好」。
           */
          render: (v: string | null, r: AssetRow) =>
            v ? (
              // contain 而不是 cover：这一列是用来「认出这是哪张图」的，
              // cover 会把非正方形的图裁掉边缘，正好裁掉最能辨认的部分。
              // 浅色底是为了让透明底立绘看得见轮廓。
              <Image
                src={v}
                width={44}
                height={44}
                style={{ objectFit: "contain", borderRadius: 4, background: "#fafafa" }}
              />
            ) : (
              <Tooltip
                title={
                  r.status === "pending_upload"
                    ? "这条还是占位记录，真实图片尚未上传"
                    : storage.data?.previewEnabled === false
                      ? "未配置 ASSET_PUBLIC_BASE_URL，无法拼出可访问地址"
                      : "该资产没有可访问的 URL"
                }
              >
                <span style={{ color: "#ccc" }}>无</span>
              </Tooltip>
            ),
        },
        {
          /*
           * 「用途」必须排在最前面，紧挨预览。
           *
           * 只看 storageKey 是看不出这张图干什么用的：种子期它是
           * `seed/miaomiao/portrait-calm.png`（名字是种子起的，未必对得上实际槽位），
           * 真实上传之后更是只剩一串哈希。传错槽位时，用途列是唯一能一眼发现的地方。
           */
          title: "用途",
          dataIndex: "usage",
          width: 230,
          render: (v: string[]) =>
            v && v.length ? (
              <Space size={[4, 4]} wrap>
                {v.map((u) => (
                  <Tag key={u} style={{ marginInlineEnd: 0 }}>
                    {u}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Tooltip title="没有任何外观、背景或头像引用它。可能是历史遗留，删除前先确认">
                <Typography.Text type="secondary">未被引用</Typography.Text>
              </Tooltip>
            ),
        },
        { title: "状态", dataIndex: "status", width: 130, render: (v) => <StatusTag value={v} /> },
        { title: "大小", dataIndex: "sizeBytes", width: 90, align: "right", render: fmtBytes },
        {
          // 后端已按 ID 补好昵称（uploadedBy 不建外键，见 asset.admin-query）。
          // 显示 uuid 对人没有意义。
          title: "上传者",
          dataIndex: "uploaderName",
          width: 110,
          render: (v: string | null) => v || "—",
        },
        { title: "上传时间", dataIndex: "createdAt", width: 120, render: (v) => <RelativeTime value={v} /> },
        { title: "ID", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
        { title: "归属", dataIndex: "ownerType", width: 90 },
        { title: "可见性", dataIndex: "visibility", width: 100, render: (v) => <StatusTag value={v} /> },
        { title: "类型", dataIndex: "mimeType", width: 100 },
        {
          /*
           * storageKey 排到最后。
           *
           * 有了「用途」列之后它就只剩排障价值了，而它又是全表最长的字段
           * （真实上传后是 `miaomiao/character/{uuid}/{uuid}/{hash}.png`）。
           * 放在中间会把「状态 / 上传者」挤出首屏，那才是每天都要看的东西。
           */
          title: "storageKey",
          dataIndex: "storageKey",
          width: 300,
          ellipsis: true,
          render: (v: string) => (
            <Tooltip title={v}>
              <Typography.Text className="mono">{v}</Typography.Text>
            </Tooltip>
          ),
        },
        {
          title: "旧 URL",
          dataIndex: "legacyUrl",
          width: 200,
          ellipsis: true,
          render: (v: string | null) => (v ? <Typography.Text className="mono">{v}</Typography.Text> : "—"),
        },
        ...(canUpload
          ? [
              {
                title: "操作",
                key: "actions",
                width: 96,
                fixed: "right" as const,
                render: (_: unknown, r: AssetRow) => (
                  <ReplaceFileButton
                    assetId={r.id}
                    onDone={() => queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/assets"] })}
                  />
                ),
              },
            ]
          : []),
      ]}
      />
    </>
  );
}
